import { useState, useEffect } from 'react';
import packageJson from '../../package.json';

const IMG_VERSION = packageJson.version;
const DEFAULT_IMAGE = '/img/card/default.png';
const imageCache = new Map();

/**
 * For <img> tags (pack images, variant pickers).
 * Use as: src={getCardImageSrc(key)} onError={handleCardImageError}
 */
export const getCardImageSrc = (key) =>
    key ? `/img/card/${key}.png?v=${IMG_VERSION}` : DEFAULT_IMAGE;

export const handleCardImageError = (e) => {
    e.target.src = DEFAULT_IMAGE;
};

/**
 * For CSS background-image (Card component).
 * Preloads the image and falls back to default.png if it doesn't exist.
 * Handles the full resolution chain: customImage → displayKey → seriesKey → default.
 * Caches results so each key is only checked once.
 */
const getPitcherKey = (seriesKey) => {
    if (!seriesKey || !seriesKey.includes('-')) return null;
    const i = seriesKey.indexOf('-');
    return `${seriesKey.slice(0, i)}-p${seriesKey.slice(i)}`;
};

const useCardImage = (card, seriesKey) => {
    const customImage = card.customImage;
    const yearBaseKey = card.year ? `${card.year}-base` : null;
    const imageKey = card.CardInventory?.displayKey || seriesKey || yearBaseKey || null;
    const pitcherKey = card.type === 'Pitcher' && !card.customImage
        ? getPitcherKey(imageKey)
        : null;

    const resolve = (key) => key ? `/img/card/${key}.png?v=${IMG_VERSION}` : DEFAULT_IMAGE;

    const [backgroundImage, setBackgroundImage] = useState(() => {
        if (customImage) return `url(${customImage})`;
        if (pitcherKey && imageCache.get(pitcherKey) === true) return `url(${resolve(pitcherKey)})`;
        if (!imageKey) return `url(${DEFAULT_IMAGE})`;
        if (pitcherKey && imageCache.get(pitcherKey) !== false) return `url(${resolve(imageKey)})`;
        const cached = imageCache.get(imageKey);
        if (cached === false) return `url(${DEFAULT_IMAGE})`;
        return `url(${resolve(imageKey)})`;
    });

    useEffect(() => {
        if (customImage) {
            setBackgroundImage(`url(${customImage})`);
            return;
        }
        if (!imageKey) {
            setBackgroundImage(`url(${DEFAULT_IMAGE})`);
            return;
        }

        const trySeriesKey = () => {
            const cached = imageCache.get(imageKey);
            if (cached === true) { setBackgroundImage(`url(${resolve(imageKey)})`); return; }
            if (cached === false) { setBackgroundImage(`url(${DEFAULT_IMAGE})`); return; }

            const img = new Image();
            img.src = resolve(imageKey);
            img.onload = () => { imageCache.set(imageKey, true); setBackgroundImage(`url(${resolve(imageKey)})`); };
            img.onerror = () => { imageCache.set(imageKey, false); setBackgroundImage(`url(${DEFAULT_IMAGE})`); };
        };

        if (pitcherKey) {
            const cached = imageCache.get(pitcherKey);
            if (cached === true) { setBackgroundImage(`url(${resolve(pitcherKey)})`); return; }
            if (cached === false) { trySeriesKey(); return; }

            const img = new Image();
            img.src = resolve(pitcherKey);
            img.onload = () => { imageCache.set(pitcherKey, true); setBackgroundImage(`url(${resolve(pitcherKey)})`); };
            img.onerror = () => { imageCache.set(pitcherKey, false); trySeriesKey(); };
            return;
        }

        trySeriesKey();
    }, [customImage, imageKey, pitcherKey]);

    return backgroundImage;
};

export default useCardImage;
